/**
 * Client-side wishlist store (localStorage only, no backend/app).
 * Mirrors the reactive pattern Etheryx already uses for <cart-count>:
 * a custom element (<wishlist-count>) re-renders itself on a dispatched event,
 * and any [data-wishlist-toggle] button anywhere in the DOM stays in sync.
 */
(() => {
  const STORAGE_KEY = 'openthinking:wishlist';
  const EVENT = 'wishlist:change';

  function readIds() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const ids = raw ? JSON.parse(raw) : [];
      return Array.isArray(ids) ? ids.map(String) : [];
    } catch (e) {
      return [];
    }
  }

  function writeIds(ids) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    document.documentElement.classList.toggle('js-wishlist-empty', ids.length === 0);
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { ids, count: ids.length } }));
  }

  function has(id) {
    return readIds().includes(String(id));
  }

  function add(id) {
    id = String(id);
    const ids = readIds();
    if (!ids.includes(id)) {
      ids.unshift(id);
      writeIds(ids);
    }
  }

  function remove(id) {
    id = String(id);
    writeIds(readIds().filter((x) => x !== id));
  }

  function toggle(id) {
    id = String(id);
    if (has(id)) {
      remove(id);
      return false;
    }
    add(id);
    return true;
  }

  function syncToggleButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const id = btn.getAttribute('data-id');
      const active = has(id);
      btn.classList.toggle('is--added', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');

      const label = btn.querySelector('[data-wishlist-toggle-label]');
      if (label) {
        const addText = btn.getAttribute('data-label-add');
        const addedText = btn.getAttribute('data-label-added');
        if (addText && addedText) {
          label.textContent = active ? addedText : addText;
        }
      }
    });
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    const id = btn.getAttribute('data-id');
    if (!id) return;
    event.preventDefault();
    toggle(id);
  });

  document.addEventListener(EVENT, syncToggleButtons);

  function init() {
    document.documentElement.classList.toggle('js-wishlist-empty', readIds().length === 0);
    syncToggleButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Wishlist = { has, add, remove, toggle, getIds: readIds, EVENT };

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this._onChange = (event) => this.render(event.detail.count);
      document.addEventListener(EVENT, this._onChange);
      this.render(readIds().length);
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT, this._onChange);
    }

    render(count) {
      this.querySelectorAll('[data-wishlist-count-num]').forEach((el) => {
        el.textContent = count;
      });
    }
  }

  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', WishlistCount);
  }
})();
